import Api from '../../lib/apiClient';
import {CacheClass} from 'memory-cache';
import {clearCachesByTag, createCache} from '../../lib/cache';
import {
    AxiosRequestConfig,
    AxiosResponseHeaders,
    RawAxiosResponseHeaders,
} from 'axios';
import {RecursivePartial} from '../../types/list';
import {getAxiosError} from '@alchemy/api';
import {EntityName} from './entities.ts';

export type RequestOptions<T = any> = {
    cache?: CacheClass<string, any>;
    transformResponse?: (r: T) => T;
    params?: any;
    signal?: AbortSignal;
};

type Cache = CacheClass<string, any>;

const caches: Record<string, Cache> = {};

type Entity = object;

export type EntityWithId = {id: string};

export type PutFn<
    Res extends object,
    Input extends object = RecursivePartial<Res>,
> = (id: string, data: Input) => Promise<Res>;
export type PostFn<
    Res extends object,
    Input extends object = RecursivePartial<Res>,
> = (data: Input) => Promise<Res>;

export function getCache(entity: string, tags?: string[]): Cache {
    return caches[entity] || (caches[entity] = createCache(tags || [entity]));
}

export async function post<
    Res extends object,
    Input extends object = RecursivePartial<Res>,
>(
    entity: string,
    data: RecursivePartial<Input>,
    options: RequestOptions<Res> = {},
): Promise<Res> {
    const _r = (await Api.post(entity, data)).data as Res;

    const r = options.transformResponse ? options.transformResponse(_r) : _r;

    if (Object.prototype.hasOwnProperty.call(r, 'id')) {
        putEntityCache(entity, (r as {id: string}).id, r);
    }

    return r;
}

export async function get<T extends Entity>(
    entity: string,
    id: string,
    config?: AxiosRequestConfig,
    options: RequestOptions<T> = {},
): Promise<T> {
    const cache = getCache(entity);

    const cached = cache.get(id);
    if (cached) {
        return cached;
    }

    const _r = (await Api.get(`/${entity}/${id}`, config)).data as T;
    const r = options.transformResponse ? options.transformResponse(_r) : _r;

    if (Object.prototype.hasOwnProperty.call(r, 'id')) {
        cache.put((r as {id: string}).id, r);
    }

    return r;
}

export async function changeOwner<T extends Entity>(
    entity: string,
    id: string,
    ownerId: string,
) {
    return (
        await Api.put(`${entity}/${id}/change-owner`, {
            ownerId,
        })
    ).data as T;
}

export async function changeHolder<T extends Entity>(
    entity: string,
    id: string,
    holderId: string | null,
) {
    const r = (
        await Api.put(`${entity}/${id}/change-holder`, {
            holderId,
        })
    ).data as T;

    clearCachesByTag(EntityName.Profile);

    return r;
}

export async function put<
    Res extends Entity,
    Input extends object = RecursivePartial<Res>,
>(
    entity: string,
    id: string,
    data: Input,
    options: RequestOptions<Res> = {},
): Promise<Res> {
    const _r = (await Api.put(`/${entity}/${id}`, data)).data as Res;

    const r = options.transformResponse ? options.transformResponse(_r) : _r;

    putEntityCache(entity, id, r);

    return r;
}

export function putEntityCache(entity: string, id: string, r: any): void {
    clearEntityCache(entity, id);
    getCache(entity).put(id, r);
}

export function clearEntityCache(entity: string, id?: string): void {
    if (id) {
        getCache(entity).del(id);
    }
    getCache(entity + ':l', [entity]).clear();
}

export async function apiDelete<T extends Entity>(
    entity: string,
    id: string,
    options: RequestOptions<T> = {},
): Promise<void> {
    clearEntityCache(entity, id);

    await Api.delete(`/${entity}/${id}`, options);
}

type ExtraDataBase = Record<string, any>;

export type ListResponse<T, ExtraData extends ExtraDataBase = {}> = {
    items: T[];
    total: number;
    next?: string;
} & ExtraData;

export type RequestConfig = {
    noCache?: boolean;
};

export async function list<
    T extends Entity,
    ExtraData extends ExtraDataBase = {},
>(
    entity: string,
    {
        cache,
        transformResponse: _transformResponse,
        ...options
    }: RequestOptions = {},
    config: RequestConfig = {},
    extraDataMapper?: ExtraDataMapper<ExtraData>,
): Promise<ListResponse<T, ExtraData>> {
    const cacheKey = `l:${options.params ? JSON.stringify(options.params) : ''}`;
    const cacheContainer = cache ?? getCache(entity + ':l', [entity]);
    const cached = cacheContainer.get(cacheKey);
    if (cached && !config.noCache) {
        return cached;
    }

    const r = (await Api.get(`/${entity}`, options)).data;

    const result = createListResponse<T, ExtraData>(r, extraDataMapper);

    if (!config.noCache) {
        cacheContainer.put(cacheKey, result);
    }

    return result;
}

type ExtraDataMapper<ExtraData> = (r: any) => ExtraData;

export function createListResponse<T, ExtraData extends ExtraDataBase = {}>(
    r: any,
    extraDataMapper?: ExtraDataMapper<ExtraData>,
): ListResponse<T, ExtraData> {
    return {
        items: r['hydra:member'],
        total: r['hydra:totalItems'],
        next: r['hydra:view'] ? r['hydra:view']['hydra:next'] : undefined,
        ...(extraDataMapper ? extraDataMapper(r) : ({} as ExtraData)),
    };
}

export async function customList<T>(
    path: string,
    cacheTags?: string[],
    options: RequestOptions = {},
): Promise<T> {
    const cache = getCache(`${path}:cl`, cacheTags);
    const cacheKey = `l:${JSON.stringify(options?.params)}`;
    const cached = cache.get(cacheKey);
    if (cached) {
        return cached;
    }

    const r = (await Api.get(`/${path}`, options)).data as T;

    cache.put(cacheKey, r);

    return r;
}

export function getValidationErrors(e: any): string[] | undefined {
    const axiosError = getAxiosError(e);
    if (axiosError) {
        if (422 === axiosError.code) {
            const response = axiosError.error.response!;
            if (
                Object.prototype.hasOwnProperty.call(
                    response.data,
                    'violations',
                )
            ) {
                return (response.data as any)['violations'].map(v => v.message);
            }
        }
    }
}

export async function fileRequest(
    url: string,
    options: RequestOptions = {},
    postBody?: object,
): Promise<{
    fileName: string | null;
    blob: Blob;
}> {
    try {
        const method = postBody ? 'POST' : 'GET';

        const response = await Api.request({
            url,
            method,
            ...options,
            responseType: 'arraybuffer',
            ...(postBody && {data: postBody}),
        });

        return {
            blob: new Blob([response.data], {
                type: response.headers['content-type'] as string | undefined,
            }),
            fileName: extractFileNameFromHeaders(response.headers) ?? null,
        };
    } catch (error: any) {
        if (error.isAxiosError) {
            const decoder = new TextDecoder();
            error.response.data = JSON.parse(
                decoder.decode(new Uint8Array(error.response.data)),
            );
        }
        throw error;
    }
}

function extractFileNameFromHeaders(
    headers: RawAxiosResponseHeaders | AxiosResponseHeaders,
): string | undefined {
    return (
        headers['content-disposition']
            ?.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)?.[1]
            ?.replace(/['"]/g, '') ?? undefined
    );
}

export enum SortOrder {
    Asc = 'asc',
    Desc = 'desc',
}

export function mergeParamsAndDefaultOrder(
    params: Record<string, any> | undefined,
    defaultOrder: Record<string, SortOrder>,
) {
    const flatOrders = {};

    for (const k in defaultOrder) {
        flatOrders[`order[${k}]`] = defaultOrder[k];
    }

    const p = params ?? {};

    return {
        ...p,
        ...flatOrders,
        ...p, // allow overriding the default 'order[xxx]' way of ordering
    };
}
